import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ShoppingBag, Menu, X, User, LogOut, Package } from "lucide-react";
import { supabase } from "../lib/supabase";
import DaxmaLogo from "../assets/images/DaxmaLogo.png";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [session, setSession] = useState(null);

  const dropdownRef = useRef(null);

  const cartCount = useSelector((state) =>
    state.cart.items.reduce((t, i) => t + i.quantity, 0),
  );

  const desktopLink = ({ isActive }) =>
    `text-sm font-medium transition ${
      isActive ? "text-black" : "text-gray-600 hover:text-black"
    }`;

  const mobileLink = ({ isActive }) =>
    `block px-4 py-3 rounded-md text-sm transition ${
      isActive ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100"
    }`;

  // 🔐 get login session
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      },
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  // 🔽 close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // 🚪 logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfileOpen(false);
    navigate("/");
  };

  return (
    <>
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 w-full h-16 bg-white z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto h-full px-5 flex items-center justify-between">
          {/* LOGO */}
          <Link to="/" className="flex items-center">
            <img
              src={DaxmaLogo}
              alt="Daxma Logo"
              className="h-6 w-auto object-contain"
            />
          </Link>

          {/* DESKTOP MENU */}
          <div className="hidden md:flex items-center gap-8">
            <NavLink to="/" className={desktopLink}>
              Home
            </NavLink>
            <NavLink to="/men" className={desktopLink}>
              Men
            </NavLink>
            <NavLink to="/couple" className={desktopLink}>
              Couple ❤️
            </NavLink>
            <NavLink to="/customize" className={desktopLink}>
              Customize
            </NavLink>
            <NavLink to="/women" className={desktopLink}>
              Women
            </NavLink>
            <NavLink to="/kids" className={desktopLink}>
              Kids
            </NavLink>
            <NavLink to="/contact" className={desktopLink}>
              Contact
            </NavLink>

            {/* CART */}
            <Link to="/cart" className="relative">
              <ShoppingBag className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* PROFILE */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="hover:opacity-70 transition">
                <User className="w-6 h-6" />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-3 w-44 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {!session ? (
                    <button
                      onClick={() => {
                        navigate(`/login?redirect=${location.pathname}`);
                        setProfileOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm hover:bg-gray-100">
                      <User size={16} />
                      Login
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          navigate("/orders");
                          setProfileOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm hover:bg-gray-100">
                        <Package size={16} />
                        My Orders
                      </button>

                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-500 hover:bg-gray-100">
                        <LogOut size={16} />
                        Logout
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* MOBILE MENU BUTTON */}
          <button className="md:hidden" onClick={() => setOpen(true)}>
            <Menu size={26} />
          </button>
        </div>
      </nav>

      {/* OVERLAY */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* MOBILE MENU */}
      <div
        className={`fixed top-0 right-0 w-64 h-full bg-white z-50 
        transform transition-transform duration-300
        ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between h-16 px-5 border-b">
          <span className="font-semibold text-lg">Menu</span>
          <button onClick={() => setOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <div className="p-4 space-y-1 pb-6">
          <NavLink onClick={() => setOpen(false)} to="/" className={mobileLink}>
            Home
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/men"
            className={mobileLink}>
            Men
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/couple"
            className={mobileLink}>
            Couple❤️
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/customize"
            className={mobileLink}>
            Customize
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/women"
            className={mobileLink}>
            Women
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/kids"
            className={mobileLink}>
            Kids
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/contact"
            className={mobileLink}>
            Contact
          </NavLink>

          <NavLink
            onClick={() => setOpen(false)}
            to="/cart"
            className={mobileLink}>
            Cart ({cartCount})
          </NavLink>

          {/* LOGIN / ORDERS */}
          {!session ? (
            <button
              onClick={() => {
                navigate(`/login?redirect=${location.pathname}`);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
              <User size={18} />
              Login
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  navigate("/orders");
                  setOpen(false);
                }}
                className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                <Package size={18} />
                My Orders
              </button>

              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  setOpen(false);
                  navigate("/");
                }}
                className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-500 hover:bg-gray-100 rounded-md">
                <LogOut size={18} />
                Logout
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
